import { api, adminApi, parseApiError } from './api';
import type {
  Transaction,
  TransactionCategory,
  TransactionFilters,
  TransactionListResponse,
  TransactionSummary,
  TrendData,
} from '@/types/transaction';
import type { ReceiptStats } from '@/types/receipt';
import type { ProcessingError, ErrorFilters, ErrorListResponse, ErrorStats, AdminStats } from '@/types/error';

// API response types (raw from backend)
interface ApiTransaction {
  id: string;
  userId: string;
  categoryId: string | null;
  receiptId: string | null;
  potId: string | null;
  type: 'expense' | 'income';
  amount: string;
  currency: string;
  description: string;
  merchantName: string | null;
  originalMessage: string;
  messageType: string;
  whatsappMessageId: string;
  confidence: string;
  transactionDate: string;
  taxDeductible: boolean;
  taxCategory: string | null;
  taxYear: number;
  createdAt: string;
  updatedAt: string | null;
  metadata: unknown;
  deletedAt: string | null;
}

interface RecentTransactionsResponse {
  transactions: ApiTransaction[];
  count: number;
}

// API response type for /api/transactions (includes embedded category object)
// This is a separate interface because the API might return different fields than the dashboard endpoints
interface ApiTransactionListItem {
  id: string;
  userId: string;
  categoryId: string | null;
  type: 'expense' | 'income';
  amount: string | number;
  currency: string;
  description: string;
  merchantName?: string | null;
  originalMessage?: string;
  // API returns transactionDate but we normalize to date
  transactionDate?: string;
  date?: string;
  createdAt: string;
  updatedAt?: string | null;
  receiptId?: string | null;
  // Category can be embedded as an object
  category?: {
    id: string;
    name: string;
    nameEn?: string;
  } | null;
}

interface ApiTransactionListResponse {
  transactions: ApiTransactionListItem[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface ApiTrendItem {
  month: string;
  income: string | number;
  expenses: string | number;
}

interface TrendsResponse {
  trends: ApiTrendItem[];
}

// Transform API trend to frontend TrendData type
function transformTrend(apiTrend: ApiTrendItem): TrendData {
  const income = typeof apiTrend.income === 'string' ? parseFloat(apiTrend.income) : apiTrend.income;
  const expense = typeof apiTrend.expenses === 'string' ? parseFloat(apiTrend.expenses) : apiTrend.expenses;
  return {
    date: apiTrend.month,
    income,
    expense,
    balance: income - expense,
  };
}

// Helper to infer category from hashtag in original message
function inferCategoryFromMessage(message: string): TransactionCategory {
  const hashtagMatch = message.match(/#(\w+)/);
  if (!hashtagMatch) return 'other';

  const hashtag = hashtagMatch[1].toLowerCase();

  const categoryMap: Record<string, TransactionCategory> = {
    food: 'food',
    alimentacao: 'food',
    transport: 'transport',
    transporte: 'transport',
    uber: 'transport',
    shopping: 'shopping',
    compras: 'shopping',
    entertainment: 'entertainment',
    entretenimento: 'entertainment',
    health: 'health',
    saude: 'health',
    education: 'education',
    educacao: 'education',
    utilities: 'utilities',
    contas: 'utilities',
    housing: 'housing',
    moradia: 'housing',
    aluguel: 'housing',
    travel: 'travel',
    viagem: 'travel',
    salary: 'salary',
    salario: 'salary',
    investment: 'investment',
    investimento: 'investment',
    'investing-figrosa': 'investment',
    freelance: 'freelance',
    gift: 'gift',
    presente: 'gift',
  };

  return categoryMap[hashtag] || 'other';
}

// Transform API transaction to frontend Transaction type
function transformTransaction(apiTx: ApiTransaction): Transaction {
  return {
    id: apiTx.id,
    userId: apiTx.userId,
    type: apiTx.type,
    category: inferCategoryFromMessage(apiTx.originalMessage),
    amount: parseFloat(apiTx.amount),
    currency: apiTx.currency,
    description: apiTx.description,
    merchant: apiTx.merchantName || undefined,
    date: apiTx.transactionDate,
    createdAt: apiTx.createdAt,
    updatedAt: apiTx.updatedAt || apiTx.createdAt,
    receiptId: apiTx.receiptId || undefined,
  };
}

// Transform API transaction list item to frontend Transaction type
// Note: category can be an object when returned from API, frontend handles both cases
function transformTransactionListItem(apiTx: ApiTransactionListItem): Omit<Transaction, 'category'> & { category: unknown } {
  const amount = typeof apiTx.amount === 'string' ? parseFloat(apiTx.amount) : apiTx.amount;
  // Handle both transactionDate and date fields from API
  const dateValue = apiTx.transactionDate || apiTx.date || '';
  return {
    id: apiTx.id,
    userId: apiTx.userId,
    type: apiTx.type,
    // Keep the embedded category object if present, otherwise infer from message
    category: apiTx.category || inferCategoryFromMessage(apiTx.originalMessage || ''),
    amount: amount,
    currency: apiTx.currency,
    description: apiTx.description,
    merchant: apiTx.merchantName || undefined,
    date: dateValue,
    createdAt: apiTx.createdAt,
    updatedAt: apiTx.updatedAt || apiTx.createdAt,
    receiptId: apiTx.receiptId || undefined,
  };
}

export const dashboardService = {
  // ============ User Dashboard ============

  /**
   * Get transaction summary for the authenticated user
   */
  async getSummary(period: 'week' | 'month' | 'year' = 'month'): Promise<TransactionSummary> {
    try {
      const response = await api.get<TransactionSummary>('/api/dashboard/summary', {
        params: { period },
      });
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get spending trends for the authenticated user
   */
  async getTrends(period: 'week' | 'month' | 'year' = 'month'): Promise<TrendData[]> {
    try {
      const response = await api.get<TrendsResponse>('/api/dashboard/trends', {
        params: { period },
      });
      // Transform API response to frontend TrendData type
      return response.data.trends.map(transformTrend);
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get recent transactions for the authenticated user
   */
  async getRecentTransactions(limit: number = 10): Promise<Transaction[]> {
    try {
      const response = await api.get<RecentTransactionsResponse>('/api/dashboard/recent', {
        params: { limit },
      });
      // Transform API response to frontend Transaction type
      return response.data.transactions.map(transformTransaction);
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get user's receipt processing stats
   */
  async getReceiptStats(): Promise<ReceiptStats> {
    try {
      const response = await api.get<ReceiptStats>('/api/dashboard/receipts/stats');
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  // ============ Transactions ============

  /**
   * Get paginated list of transactions
   */
  async getTransactions(filters: TransactionFilters = {}): Promise<TransactionListResponse> {
    try {
      const response = await api.get<ApiTransactionListResponse>('/api/transactions', {
        params: filters,
      });
      // Transform API response to frontend Transaction type
      return {
        ...response.data,
        transactions: response.data.transactions.map(transformTransactionListItem) as Transaction[],
      };
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get a single transaction by ID
   */
  async getTransaction(id: string): Promise<Transaction> {
    try {
      const response = await api.get<Transaction>(`/api/transactions/${id}`);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Update a transaction
   */
  async updateTransaction(id: string, data: Partial<Transaction>): Promise<Transaction> {
    try {
      const response = await api.patch<Transaction>(`/api/transactions/${id}`, data);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Delete a transaction
   */
  async deleteTransaction(id: string): Promise<void> {
    try {
      await api.delete(`/api/transactions/${id}`);
    } catch (error) {
      throw parseApiError(error);
    }
  },

  // ============ Admin Dashboard ============

  /**
   * Get admin dashboard stats
   */
  async getAdminStats(): Promise<AdminStats> {
    try {
      const response = await adminApi.get<AdminStats>('/admin/stats');
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get processing errors (admin)
   */
  async getErrors(filters: ErrorFilters = {}): Promise<ErrorListResponse> {
    try {
      const response = await adminApi.get<ErrorListResponse>('/admin/errors', {
        params: filters,
      });
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get error stats (admin)
   */
  async getErrorStats(): Promise<ErrorStats> {
    try {
      const response = await adminApi.get<ErrorStats>('/admin/errors/stats');
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Update error status (admin)
   */
  async updateError(id: string, data: Partial<ProcessingError>): Promise<ProcessingError> {
    try {
      const response = await adminApi.patch<ProcessingError>(`/admin/errors/${id}`, data);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get a single error by ID (admin)
   */
  async getError(id: string): Promise<ProcessingError> {
    try {
      const response = await adminApi.get<ProcessingError>(`/admin/errors/${id}`);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Retry failed processing (admin)
   */
  async retryProcessing(errorId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await adminApi.post<{ success: boolean; message: string }>(
        `/admin/errors/${errorId}/retry`
      );
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },
};

export default dashboardService;
